import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { cx } from 'linaria';
import { analyticsUtils } from 'utils';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { useOptimisedImages } from '@navi-toolbox/hooks';
import { ImageContainer, item, mainRounding, specialRounding } from './styles';
import { getTitle } from '../page-model/ducks/get-model';
import { ProjectContent } from './project-content';
import { Direction } from './types';
import { useHasServicesLinks } from 'common/hooks/use-has-services-links';

export const DATA_QA_PROJECT_ITEM = 'project-item';
export const DATA_QA_PROJECT_IMAGE = 'project-image';
export const DATA_QA_PROJECT_IMAGE_SOURCE = 'project-image-source';

type Props = {
  dir: Direction;
  title: string;
  link: string;
  description: string;
  image: string;
  color: string;
  idx: number;
};

export const Project: FC<Props> = ({ dir, title, description, link, image, color, idx }) => {
  const { t } = useTranslation();
  const blockTitle = useSelector(getTitle);
  const { desktop, largeDesktop } = useBreakpoints() || {};
  const handleClick = useCallback(
    (text: string) => {
      analyticsUtils.sendEvent('GA:clickButton', {
        elementType: 'button',
        elementBlock: 'shopPage', // блок, в котором расположен элемент
        elementName: `${blockTitle} / ${t('projectsCenters')}`, // название блока
        elementText: text, // заголовок карточки
      });
    },
    [blockTitle, t],
  );
  const optimisedImage = useOptimisedImages({
    src: image,
    sizes: {
      rest: {
        w: 'auto',
      },
    },
  });

  const imageSet = image ? `${optimisedImage.rest?.xx} 2x, ${optimisedImage.rest?.x} 1x` : undefined;

  const hasLink = useHasServicesLinks();
  const Tag = hasLink ? 'a' : 'span';

  return (
    <Tag
      className={cx(item, dir)}
      style={{ '--box-color': color } as React.CSSProperties}
      href={link}
      target="_blank"
      onClick={() => handleClick(title)}
      data-qa={DATA_QA_PROJECT_ITEM}
      rel="noopener"
    >
      <ImageContainer className={idx === 0 || !(desktop || largeDesktop) ? mainRounding : specialRounding}>
        <picture>
          <source srcSet={imageSet} data-qa={DATA_QA_PROJECT_IMAGE_SOURCE} />
          <img
            src={optimisedImage.rest?.x ?? undefined}
            srcSet={imageSet}
            alt={title}
            data-qa={DATA_QA_PROJECT_IMAGE}
          />
        </picture>
      </ImageContainer>
      <ProjectContent title={title} description={description} dir={dir} hideLearnMore={!hasLink} />
    </Tag>
  );
};
